import { LitElement, css, html, TemplateResult, PropertyValues } from 'lit'
import { property, customElement, state } from 'lit/decorators.js'
import '@maersk-global/mds-components-core'
import { AlertConfig, AlertStatus } from '../utils/alert-configuration'
import { VesselAlert } from '../../../src/models/vessel-alerts-model'
import { GetFormattedDateDayMonthTimeNoYear } from '../../../src/utils/helpers'

@customElement('voy-alert-message')
export class VoyageAlertMessage extends LitElement {
    @property({ type: Object }) config: AlertConfig | null = null
    @property({ type: Object }) alert: VesselAlert | null = null
    @property({ type: Boolean }) expandByDefault: boolean = false
    @property({ type: Boolean }) toggleable: boolean = true
    @state() private _isExpanded = false

    constructor() {
        super()
    }

    static get styles() {
        return [
            css`
                :host {
                    width: 100%;
                }

                mc-notification::part(notification) {
                    box-shadow: 0px 4px 2px rgba(0, 0, 0, 0.25);
                    cursor: pointer;
                }

                mc-notification:not(.expanded)::part(notification) {
                    transition: all 0.2s ease;
                }

                mc-notification:not(.expanded):hover::part(notification) {
                    box-shadow: 0px 6px 4px rgba(0, 0, 0, 0.3);
                }

                mc-notification:hover mc-icon::part(icon) {
                    transform: scale(1.2);
                }

                mc-notification:not(.expanded)::part(notification) {
                    height: 44px;
                }

                .wrapper {
                    display: flex;
                    flex-direction: row;
                    justify-content: space-between;
                    gap: 2px;
                }

                .alert-body {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    font-family: MaerskText;
                    font-size: 14px;
                    gap: 8px;
                    width: 100%;
                    min-width: 0;
                    margin-top: 2px;
                }

                .alert-body.minimized {
                    gap: 0;
                }

                .button--action {
                    text-decoration: underline;
                    cursor: pointer;
                    background: none;
                    border: none;
                    padding: 0;
                    font-family: inherit;
                    font-size: inherit;
                }

                .action-list {
                    list-style: none;
                    margin: 0;
                    padding: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }

                .title {
                    font-weight: bold;
                    line-height: 20px;
                    width: 100%;
                    min-width: 0;
                }

                .title.minimized {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .header {
                    display: grid;
                    grid-template-columns: 1fr;
                }

                .message {
                    margin: 0;
                    line-height: 20px;
                }

                .expand-content {
                    display: block;
                }

                .expand-content.hidden {
                    display: none;
                }

                .mc-notification__actions > *::after {
                    content: '';
                }
            `,
        ]
    }

    connectedCallback() {
        super.connectedCallback()
        this._isExpanded = this.expandByDefault
    }

    disconnectedCallback() {
        super.disconnectedCallback()
    }

    updated(changedProperties: PropertyValues) {
        super.updated(changedProperties)

        if (
            changedProperties.has('expandByDefault') &&
            this.expandByDefault &&
            !this._isExpanded
        ) {
            this._isExpanded = true
            this.requestUpdate()
        }
    }

    getIcon(appearance: AlertStatus): string {
        switch (appearance) {
            case 'error':
                return 'exclamation-octagon'
            case 'warning':
                return 'exclamation-triangle'
            case 'info':
                return 'info-circle'
            case 'success':
                return 'check-circle'
            default:
                return 'exclamation-octagon'
        }
    }

    private _toggleExpanded(event: Event) {
        // Prevent toggle if clicking on close button or action button
        const target = event.target as HTMLElement
        if (
            target.closest('mc-icon[name="x-lg"]') ||
            target.closest('.button--action')
        ) {
            return
        }

        this._isExpanded = !this._isExpanded

        // Emit event to parent to handle layout changes
        this.dispatchEvent(
            new CustomEvent('alert-expanded', {
                detail: {
                    alertId: this.alert?.id,
                    expanded: this._isExpanded,
                },
                bubbles: true,
                composed: true,
            }),
        )
    }

    render() {
        if (!this.config || !this.alert) {
            return html``
        }

        const { appearance, title, closable, action, actions, showTime, subText = "" } = this.config

        // if there is no title, use the alert message as the minimized title
        const minimizedTitle = this._isExpanded
            ? title
            : title || this.alert?.message

        const showTimeInTitle = showTime && this.alert?.time && this._isExpanded

        return html`
            <mc-notification
                appearance=${appearance}
                icon=${this.getIcon(appearance)}
                ?closable=${this._isExpanded ? closable : false}
                @close=${this.close}
                @click=${this.toggleable
                    ? this._toggleExpanded
                    : undefined}
                fit="medium"
                class=${this._isExpanded ? 'expanded' : ''}
                actionsposition="right"
            >
                <div class="wrapper">
                    <div
                        class="alert-body ${this._isExpanded
                            ? ''
                            : 'minimized'}"
                    >
                        ${
                            // header class needs to be there so that the title can be properly truncated
                            minimizedTitle
                                ? html` <div class="header">
                                      <span
                                          class="title ${this._isExpanded
                                              ? ''
                                              : 'minimized'}"
                                          >${minimizedTitle}
                                          ${showTimeInTitle
                                              ? html`<span class="time"
                                                        >${GetFormattedDateDayMonthTimeNoYear(
                                                            this.alert?.time!,
                                                        )}</span
                                                    >
                                                    UTC`
                                              : ''}</span
                                      >
                                      ${subText ? html`<span class="messageNotification">${subText}</span>` : ''}
                                  </div>`
                                : ''
                        }

                        <div
                            class="expand-content ${this._isExpanded
                                ? ''
                                : 'hidden'}"
                        >
                            ${this.alert?.message
                                ? html`<p class="message">
                                      ${this.alert?.message}
                                  </p>`
                                : ''}
                            ${action
                                ? html`<button
                                      @click=${action.onClick}
                                      class="button--action"
                                  >
                                      ${action.text}
                                  </button>`
                                : ''}
                            ${actions?.length
                                ? html`<ul class="action-list">
                                      ${actions.map(a => html`<li><button
                                          @click=${a.onClick}
                                          class="button--action"
                                      >${a.text}</button></li>`)}
                                  </ul>`
                                : ''}
                        </div>
                    </div>
                    ${this.toggleable
                        ? html`<mc-icon icon="expand" size="16"></mc-icon>`
                        : ''}
                </div>
            </mc-notification>
        `
    }

    close() {
        this.dispatchEvent(
            new Event('close', {
                bubbles: true,
                composed: true,
            }),
        )
    }
}
